/* The subscription signup's city field (sections/sub-bundles.liquid): the shopper types freely and a list under the
   field suggests matching Israeli localities from assets/dg-cities.json (the official list the cart drawer's "האם
   התכוונתם ל…" also reads). Whether a subscription goes there, on which day and for what delivery fee is the
   subscription service's answer for the place picked (/apps/loyalty/sub-city); deliveryFee() is the fee rule the
   form shows, the same one the service charges. buildIndex can still put given places first ({ name: day }).
   Also loads in Node (module.exports) for tests/sub-city-search.test.cjs. */
(() => {
  if (typeof window !== 'undefined' && window.SubCitySearch) return;

  const FINALS = { 'ך': 'כ', 'ם': 'מ', 'ן': 'נ', 'ף': 'פ', 'ץ': 'צ' };
  // Six rows fit under the field without scrolling the list, even on a phone with the keyboard open.
  const LIMIT = 6;

  // The form every comparison uses: no niqqud, quotes or direction marks, punctuation as spaces, final letters as
  // regular ones (so "רמת ג" already matches "רמת גן"), doubled ו/י as one (קריית/קרית, תקווה/תקוה), Latin in
  // lowercase.
  function key(text) {
    return String(text ?? '')
      .normalize('NFC')
      .replace(/[֑-ׇ]|\p{Cf}/gu, '')
      .replace(/["'״׳`”“‘’]/g, '')
      .replace(/[-־–—_.,()/]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/[ךםןףץ]/g, letter => FINALS[letter])
      .replace(/וו/g, 'ו')
      .replace(/יי/g, 'י');
  }

  // served: { name: weekday } for the subscription places. data: the official list ({ cities: [[hebrew, english]…],
  // aliases, common }), or null when it could not load, and then only the subscription places are offered.
  // A subscription place keeps the name the service knows even where the official spelling differs
  // ("תל אביב-יפו" / "תל אביב - יפו"); a subscription name that is only a short form of another subscription place
  // ("תל אביב") is still understood but not suggested twice.
  function buildIndex(served, data) {
    const places = [];
    const byKey = new Map();
    const byEnglish = new Map();
    const aliases = new Map();
    const common = new Set((Array.isArray(data?.common) ? data.common : []).map(key));
    const add = (name, day, english) => {
      const placeKey = key(name);
      if (!placeKey) return;
      let place = byKey.get(placeKey);
      if (!place) {
        place = { name, day, key: placeKey, english: '', common: common.has(placeKey), hidden: false };
        byKey.set(placeKey, place);
        places.push(place);
      }
      const englishKey = key(english);
      if (englishKey && !place.english) {
        place.english = englishKey;
        if (!byEnglish.has(englishKey)) byEnglish.set(englishKey, place);
      }
    };
    for (const [name, day] of Object.entries(served || {})) {
      if (Number.isInteger(day)) add(name, day, '');
    }
    for (const entry of Array.isArray(data?.cities) ? data.cities : []) {
      const [hebrew, english] = Array.isArray(entry) ? entry : [];
      if (typeof hebrew === 'string') add(hebrew, null, typeof english === 'string' ? english : '');
    }
    for (const [alias, target] of Object.entries(data?.aliases || {})) {
      const place = byKey.get(key(target));
      const aliasKey = key(alias);
      if (!place || !aliasKey) continue;
      aliases.set(aliasKey, place);
      const twin = byKey.get(aliasKey);
      if (twin && twin !== place && twin.day !== null && place.day !== null) twin.hidden = true;
    }
    return { places, byKey, byEnglish, aliases };
  }

  // 0 the whole name, 1 the name starts with it, 2 a later word starts with it, 3 it appears inside (2 letters or
  // more), -1 no match.
  function closeness(form, typed) {
    if (!form) return -1;
    if (form === typed) return 0;
    if (form.startsWith(typed)) return 1;
    if (form.includes(` ${typed}`)) return 2;
    return typed.length >= 2 && form.includes(typed) ? 3 : -1;
  }

  function aliasOf(index, typed) {
    return index.aliases.get(typed) || index.aliases.get(typed.replace(/ /g, '')) || null;
  }

  const shape = place => ({ name: place.name, day: place.day });

  // Up to `limit` places for what is typed so far, best first: the closer match, then a subscription place, then a
  // common city, then the shorter name.
  function suggest(index, text, limit = LIMIT) {
    const typed = key(text);
    if (!typed || !index) return [];
    const alias = aliasOf(index, typed);
    const found = [];
    for (const place of index.places) {
      if (place.hidden) continue;
      const near = [closeness(place.key, typed), closeness(place.english, typed)].filter(value => value >= 0);
      if (place === alias) near.push(0);
      if (near.length) found.push({ place, near: Math.min(...near) });
    }
    found.sort((a, b) => (a.near - b.near)
      || ((b.place.day !== null) - (a.place.day !== null))
      || (b.place.common - a.place.common)
      || (a.place.name.length - b.place.name.length)
      || a.place.name.localeCompare(b.place.name, 'he'));
    return found.slice(0, limit).map(({ place }) => shape(place));
  }

  // One delivery's fee from the zone terms the subscription service answers with ({cost, free, small_fee,
  // small_under}): what a regular order of the same products pays, read from their full price. The same rule as
  // loyalty-service/lib/subs.js deliveryFee, which charges it; no terms = no fee (a free-delivery subscription).
  function deliveryFee(terms, fullPrice) {
    if (!terms) return 0;
    const price = Number(fullPrice) || 0;
    let fee = Math.max(0, Number(terms.cost) || 0);
    if (Number(terms.free) > 0 && price >= Number(terms.free)) fee = 0;
    if (fee > 0 && Number(terms.small_fee) > 0 && price < Number(terms.small_under)) fee = Number(terms.small_fee);
    return fee;
  }

  // The place a whole typed name stands for, or null: exact names in any spelling, short forms (ת"א), English names.
  function resolve(index, text) {
    const typed = key(text);
    if (!typed || !index) return null;
    const place = index.byKey.get(typed) || aliasOf(index, typed) || index.byEnglish.get(typed);
    return place ? shape(place) : null;
  }

  // Wires the field. input: the text box (role=combobox); list: the suggestions (role=listbox); note(place): optional
  // small text beside a suggestion; onChange(place|null, text, settled): after every keystroke and pick (settled is
  // true once the shopper picked or left the field). The official list loads on the first use (or load()).
  function attach({ input, list, served, citiesUrl, note, onChange }) {
    let index = buildIndex(served, null);
    let loading = null;
    let shown = [];
    let active = -1;
    const idBase = list.id || 'sub-city-list';

    const report = settled => onChange(resolve(index, input.value), input.value, settled);

    function load() {
      if (loading || !citiesUrl) return;
      loading = fetch(citiesUrl, { credentials: 'omit' })
        .then(response => (response.ok ? response.json() : Promise.reject(new Error(`HTTP_${response.status}`))))
        .then(data => {
          if (!Array.isArray(data?.cities)) throw new Error('CITIES_UNAVAILABLE');
          index = buildIndex(served, data);
          if (document.activeElement === input && input.value.trim()) render();
          report(document.activeElement !== input);
        })
        .catch(() => {
          // Forgotten, so the next focus asks again; the subscription places keep working meanwhile.
          loading = null;
        });
    }

    function close() {
      list.hidden = true;
      list.replaceChildren();
      shown = [];
      active = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }

    function highlight(position) {
      active = position;
      Array.from(list.children).forEach((option, n) => option.setAttribute('aria-selected', n === position ? 'true' : 'false'));
      if (position >= 0) input.setAttribute('aria-activedescendant', `${idBase}-${position}`);
      else input.removeAttribute('aria-activedescendant');
    }

    function pick(place, byPointer) {
      input.value = place.name;
      close();
      onChange(place, place.name, true);
      // A tap closes the phone keyboard: the fields after this one are steppers. The keyboard path keeps focus.
      if (byPointer) input.blur();
    }

    function render() {
      shown = suggest(index, input.value);
      if (!shown.length) {
        close();
        return;
      }
      list.replaceChildren(...shown.map((place, position) => {
        const option = document.createElement('li');
        option.id = `${idBase}-${position}`;
        option.setAttribute('role', 'option');
        option.className = place.day === null ? 'sb-city__opt sb-city__opt--closed' : 'sb-city__opt';
        const name = document.createElement('span');
        name.textContent = place.name;
        option.append(name);
        if (note) {
          const side = document.createElement('small');
          side.textContent = note(place);
          option.append(side);
        }
        // Keeps the focus in the field, so the list is still there when the click lands.
        option.addEventListener('mousedown', event => event.preventDefault());
        // Only a mouse that really moves takes over the highlight: a list opening (or scrolling) under a still mouse
        // must not, or Enter would pick that row instead of the first.
        option.addEventListener('mousemove', event => {
          if (event.movementX || event.movementY) highlight(position);
        });
        option.addEventListener('click', () => pick(place, true));
        return option;
      }));
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      highlight(0);
    }

    input.addEventListener('focus', () => {
      load();
      if (input.value.trim() && !resolve(index, input.value)) render();
    });
    input.addEventListener('input', () => {
      load();
      render();
      report(false);
    });
    input.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (list.hidden) {
          render();
          return;
        }
        const step = event.key === 'ArrowDown' ? 1 : -1;
        highlight((active + step + shown.length) % shown.length);
      } else if (event.key === 'Enter' && !list.hidden && active >= 0) {
        event.preventDefault();
        pick(shown[active], false);
      } else if (event.key === 'Escape' && !list.hidden) {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    });
    input.addEventListener('blur', () => {
      close();
      report(true);
    });

    return { load, place: () => resolve(index, input.value), refresh: () => report(false) };
  }

  const api = { key, buildIndex, suggest, resolve, deliveryFee, attach };
  if (typeof window !== 'undefined') window.SubCitySearch = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
